// Service-worker update detection (arc 101 slice 09, task 59).
//
// `public/sw.js` no longer `skipWaiting()`s on install — a new build installs and
// WAITS so it can't swap the shell out mid-session (evicted chunks → load errors,
// lost input). This is the page half: watch for the waiting worker, expose it as
// `ready`, and on the user's accept tell it to take over (`postMessage`) — the
// page reloads on the `controllerchange` that follows.
//
// Every browser touch sits behind an injected `SwUpdateEnv`, so the state-machine
// is unit-testable in happy-dom (no `navigator.serviceWorker` there).
// `browserSwUpdateEnv` wires the seam to the real APIs; `<UpdatePrompt/>` and the
// tests pick which to pass.

import { createSignal } from 'solid-js';
import { registerServiceWorker } from './push';

/** The message the page posts to a waiting worker to make it take over. `sw.js`
 *  RE-STATES the `'SKIP_WAITING'` type (it can't import) — held honest by
 *  `sw.lifecycle.test.ts`. */
export const SKIP_WAITING_TYPE = 'SKIP_WAITING' as const;
export const SKIP_WAITING_MESSAGE = { type: SKIP_WAITING_TYPE } as const;

/** A worker as the watcher touches it: its lifecycle state, a channel to tell it
 *  to skip waiting, and a `statechange` hook to catch it reaching `installed`. */
export type SwWorkerLike = {
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: 'statechange', listener: () => void): void;
};

/** A registration as the watcher touches it. */
export type SwRegistrationLike = {
  readonly waiting: SwWorkerLike | null;
  readonly installing: SwWorkerLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
};

/** Browser seam — the update state-machine is pure over this. */
export type SwUpdateEnv = {
  /** May reject (SW disabled, private window, a throwing `sw.js`) or resolve
   *  `undefined`; both leave the watcher inert. The `undefined` is OBSERVED, not
   *  hypothetical: GlitchTip issue 297 (developerz project, short id 108),
   *  `ClientError: can't access property "waiting", r is undefined`, read
   *  2026-09-27 via `bun run infra psql --db glitchtip`. */
  register(): Promise<SwRegistrationLike | undefined>;
  /** Is a worker already controlling this page? `false` on the first-ever install,
   *  where a freshly-`installed` worker is NOT an update to prompt about. */
  isControlled(): boolean;
  onControllerChange(listener: () => void): void;
  reload(): void;
};

/** What `<UpdatePrompt/>` consumes. */
export type SwUpdate = {
  /** `true` once a NEW worker is installed and waiting — an update to offer. */
  ready(): boolean;
  /** Hand control to the waiting worker; the page reloads on `controllerchange`. */
  apply(): void;
};

/**
 * Watch a registration for an update. Pure over the injected env. Flips `ready`
 * when a new worker reaches `installed` while an old one still controls the page
 * (or one is already `waiting` at attach time), and reloads exactly once when the
 * accepted worker takes control.
 */
export function createSwUpdate(env: SwUpdateEnv): SwUpdate {
  const [ready, setReady] = createSignal(false);
  let waiting: SwWorkerLike | null = null;

  const arm = (worker: SwWorkerLike): void => {
    waiting = worker;
    setReady(true);
  };

  // Reload once a NEW worker takes control. Two guards:
  //   - the FIRST controller acquisition on an uncontrolled page is the first-ever
  //     install claiming us (`clients.claim()` in `activate`) — nothing was swapped
  //     out, so reloading there would discard typed input for no reason;
  //   - `reloading` so a second `controllerchange` can't reload-loop.
  let hadController = env.isControlled();
  let reloading = false;
  env.onControllerChange(() => {
    if (!hadController) {
      hadController = true;
      return;
    }
    if (reloading) return;
    reloading = true;
    env.reload();
  });

  // Best-effort, like `main.tsx`'s registration: no worker means no update to
  // offer, never a crash. Without a rejection handler a refused registration
  // escaped as an unhandled rejection, and an `undefined` registration threw on
  // `reg.waiting` (GlitchTip 297, above). A console line, not the client-error
  // sink — a browser that won't run a worker is not an SPA defect. The rejection
  // handler is `then`'s second argument, so a throw inside `watch` (a real bug)
  // is NOT relabelled as a refused registration.
  void env.register().then(
    (reg) => {
      if (reg !== undefined) watch(reg);
    },
    (e: unknown) => {
      console.warn('[sw] registration failed; update prompt stays inert', e);
    },
  );

  function watch(reg: SwRegistrationLike): void {
    // Installed while the page was away — an update is waiting right now.
    const existing = reg.waiting;
    if (existing && env.isControlled()) arm(existing);
    // A worker that installs from here: prompt when it reaches `installed` WITH an
    // existing controller (else it's the first install, not an update).
    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && env.isControlled()) arm(installing);
      });
    });
  }

  return {
    ready,
    apply: () => waiting?.postMessage(SKIP_WAITING_MESSAGE),
  };
}

/** Is there a service-worker container to watch at all? `false` in dev tooling /
 *  happy-dom / an old browser, so `<UpdatePrompt/>` stays inert there. */
export function swUpdateSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/** Wire the seam to the real browser APIs. Call only where `swUpdateSupported()`. */
export function browserSwUpdateEnv(): SwUpdateEnv {
  const container = navigator.serviceWorker;
  const adaptWorker = (worker: ServiceWorker): SwWorkerLike => ({
    get state() {
      return worker.state;
    },
    postMessage: (message) => worker.postMessage(message),
    addEventListener: (_type, listener) => worker.addEventListener('statechange', listener),
  });
  const adaptReg = (reg: ServiceWorkerRegistration): SwRegistrationLike => ({
    get waiting() {
      return reg.waiting ? adaptWorker(reg.waiting) : null;
    },
    get installing() {
      return reg.installing ? adaptWorker(reg.installing) : null;
    },
    addEventListener: (_type, listener) => reg.addEventListener('updatefound', listener),
  });
  return {
    // Idempotent with `main.tsx`'s registration (the browser dedups by scope+url):
    // this call just yields the handle to attach `updatefound` to. The callback
    // takes `| undefined` on purpose: `registerServiceWorker` (push.ts) is typed
    // `Promise<ServiceWorkerRegistration>`, narrower than what browsers deliver
    // (GlitchTip 297, on `SwUpdateEnv.register`).
    register: () =>
      registerServiceWorker(container).then((reg: ServiceWorkerRegistration | undefined) =>
        reg === undefined ? undefined : adaptReg(reg),
      ),
    isControlled: () => container.controller !== null,
    onControllerChange: (listener) => container.addEventListener('controllerchange', listener),
    reload: () => window.location.reload(),
  };
}
